import { connect } from 'react-redux';
import React, { useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { format, addYears, addDays } from 'date-fns';
import { Save, X, Pencil, Trash2 } from 'lucide-react';

import { Actions } from '../../store/actions/facilityActions';

import { Button } from '../ui/button';
import { Select } from '../ui/newSelect';
import { TableRow, TableCell } from '../ui/table';

const FacilityMetricRow = ({
  metric,
  reportingPeriods,
  facMetrics,
  saveFacilityMetric,
  showFacilityMetricDeleteModal,
  list,
  facility,
  canEdit,
  canDelete,
}) => {
  const facilityMetricDates = list.map((date) => date.startDate);
  const filteredReportingPeriods = reportingPeriods.filter((report) => !facilityMetricDates.includes(report.startDate));
  const [editing, setEditing] = useState(metric.id === metric.uuid);
  const [editedState, setEditedState] = useState(null);

  if (editing) {
    return (
      <TableRow id='saveFacilityMetric-Row'>
        <TableCell style={{ textAlign: 'left' }}>
          <Select
            options={filteredReportingPeriods.map((period) => ({
              value: period.value,
              label: period.label,
              startDate: period.startDate,
              endDate: period.endDate,
            }))}
            value={editedState?.year}
            onChange={(selectedYear) => {
              const selectedOption = filteredReportingPeriods.find((opt) => opt.value === selectedYear);
              if (selectedOption) {
                setEditedState({
                  ...editedState,
                  year: selectedOption.value,
                  startDate: selectedOption.startDate,
                  endDate: selectedOption.endDate,
                });
              }
            }}
            placeholder='Select reporting period'
          />
        </TableCell>
        {facMetrics?.metric1 && (
          <TableCell>
            <NumericFormat
              name='metric1'
              inputMode='numeric'
              thousandSeparator={true}
              className='w-full px-3 py-2 border rounded-md'
              defaultValue={facility?.facilityM2}
              onValueChange={(values) => setEditedState({ ...editedState, metric1: values.value })}
              value={editedState?.metric1}
            />
          </TableCell>
        )}
        {facMetrics?.metric2 && (
          <TableCell>
            <NumericFormat
              thousandSeparator={true}
              className='w-full px-3 py-2 border rounded-md'
              inputMode='numeric'
              onValueChange={(values) => setEditedState({ ...editedState, metric2: values.value })}
              value={editedState?.metric2}
            />
          </TableCell>
        )}
        {facMetrics?.metric3 && (
          <TableCell>
            <NumericFormat
              thousandSeparator={true}
              className='w-full px-3 py-2 border rounded-md'
              inputMode='numeric'
              onValueChange={(values) => setEditedState({ ...editedState, metric3: values.value })}
              value={editedState?.metric3}
            />
          </TableCell>
        )}
        <TableCell className='text-right'>
          <div className='flex justify-end gap-2'>
            <Button
              variant='ghost'
              size='icon'
              id='save-fac-metric'
              onClick={() => {
                saveFacilityMetric({ ...metric, ...editedState, status: 'Active' });
                setEditedState(null);
                setEditing(false);
              }}>
              <Save size={18} strokeWidth={1.5} />
            </Button>
            {metric.id !== metric.uuid && (
              <Button
                variant='ghost'
                size='icon'
                id='cancel-save-fac-metric'
                onClick={() => {
                  setEditedState(null);
                  setEditing(false);
                }}>
                <X size={18} strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell style={{ textAlign: 'left' }}>
        {metric?.startDate && format(new Date(metric?.startDate), 'do MMM yyyy')} -{' '}
        {metric?.endDate && format(new Date(metric?.endDate), 'do MMM yyyy')}
      </TableCell>
      {facMetrics.metric1 && (
        <TableCell>
          {metric && metric.metric1 ? metric.metric1.replace(/(?<!\.\d*)(?<=\d)(?=(?:\d{3})+(?!\d))/g, ',') : 'N/A'}
        </TableCell>
      )}
      {facMetrics.metric2 && (
        <TableCell>
          {metric && metric.metric2 ? metric.metric2.replace(/(?<!\.\d*)(?<=\d)(?=(?:\d{3})+(?!\d))/g, ',') : 'N/A'}
        </TableCell>
      )}
      {facMetrics.metric3 && (
        <TableCell>
          {metric && metric.metric3 ? metric.metric3.replace(/(?<!\.\d*)(?<=\d)(?=(?:\d{3})+(?!\d))/g, ',') : 'N/A'}
        </TableCell>
      )}
      <TableCell className='text-right'>
        <div className='flex justify-end gap-2'>
          <Button variant='ghost' size='icon' id='edit-fac-metric' onClick={() => { setEditing(true); setEditedState(metric); }}>
            <Pencil size={18} strokeWidth={1.5} />
          </Button>
          <Button
            variant='ghost'
            size='icon'
            id='delete-fac-metric'
            onClick={() => showFacilityMetricDeleteModal(true, metric)}>
            <Trash2 size={18} strokeWidth={1.5} />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const mapStateToProps = (state) => {
  const organisation =
    state.organisation.details || state.organisation.list.find((item) => item.id === state.currentOrganisation);
  const thisYear = format(new Date(), 'yyyy');
  const reportingYearEndMonth = organisation?.reportingMonth || organisation?.reportingYearEndMonth; // property name changes depending on Symph or Dynamo
  const reportingYearEndDay = organisation?.reportingDay || organisation?.reportingYearEndDay; // property name changes depending on Symph or Dynamo

  const lastYear =
    format(new Date(), 'MM-dd') >= `${reportingYearEndMonth}-${reportingYearEndDay}` ? +thisYear + 1 : thisYear;

  const reportingPeriods = [];

  if (organisation && reportingYearEndMonth && reportingYearEndDay) {
    for (var i = lastYear; i >= 2014; i--) {
      const endDate = new Date(`${i}-${reportingYearEndMonth}-${reportingYearEndDay}`);
      const startDate = addDays(addYears(endDate, -1), 1);

      reportingPeriods.push({
        year: i,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        label: `${format(startDate, 'do MMM yyyy')} - ${format(endDate, 'do MMM yyyy')}`,
        value: i,
      });
    }
  }

  return {
    reportingPeriods,
    facilityModal: state?.facility?.facilityDataDeleteModal,
    canEdit: !!state?.organisation?.permissions?.facilityMetrics?.edit,
    canDelete: !!state?.organisation?.permissions?.facilityMetrics?.delete,
  };
};

const mapDispatchToProps = (dispatch) => ({
  saveFacilityMetric: (facilityMetric) => dispatch(Actions.saveFacilityMetric(facilityMetric)),
  showFacilityMetricDeleteModal: (status, item) => dispatch(Actions.showFacilityMetricDeleteModal(status, item)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FacilityMetricRow);
